import { useMemo, useState } from "react";
import { Search } from "lucide-react";
import { useModels } from "@/hooks/useModels";
import { ModelCard } from "@/components/site/ModelCard";
import { MaskLines } from "@/components/site/Motion";

const LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

export default function Models() {
  const { models, loading } = useModels();
  const [q, setQ] = useState("");
  const [letter, setLetter] = useState("");
  const available = useMemo(() => new Set(models.map((m) => m.name[0])), [models]);
  const shown = models.filter((m) => (!letter || m.name.startsWith(letter)) && m.name.toLowerCase().includes(q.toLowerCase()));

  return (
    <main data-testid="models-page" className="min-h-screen bg-white px-5 pb-28 pt-32 text-[#1c1418] md:px-10">
      <div className="mx-auto max-w-[1400px]">
        <div className="flex flex-col justify-between gap-6 border-b-2 border-[#1c1418] pb-6 md:flex-row md:items-end">
          <MaskLines lines={[<>Models<span className="text-[#1c1418]">.</span></>]} className="font-display text-[22vw] uppercase leading-[0.85] md:text-[11rem]" />
          <div className="flex flex-col gap-3 md:items-end">
            <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#1c1418]/70" data-testid="models-count">{shown.length} / {models.length} faces · Women</span>
            <label className="flex items-center gap-2 rounded-full border border-[#1c1418] px-4 py-2">
              <Search className="h-4 w-4" />
              <input data-testid="models-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search a name"
                className="w-44 bg-transparent font-mono text-xs uppercase tracking-[0.15em] outline-none placeholder:text-[#1c1418]/50" />
            </label>
          </div>
        </div>
        <div className="no-scrollbar mt-5 flex gap-1 overflow-x-auto" data-testid="models-az-filter">
          <button onClick={() => setLetter("")} data-testid="az-all"
            className={`shrink-0 px-2.5 py-1 font-mono text-xs ${!letter ? "bg-[#1c1418] text-[#f4ede6]" : "hover:underline"}`}>ALL</button>
          {LETTERS.map((l) => (
            <button key={l} disabled={!available.has(l)} onClick={() => setLetter(l)} data-testid={`az-${l}`}
              className={`shrink-0 px-2.5 py-1 font-mono text-xs transition-colors disabled:opacity-25 ${letter === l ? "bg-[#1c1418] text-white" : "hover:underline"}`}>{l}</button>
          ))}
        </div>
        {loading && <p className="mt-16 font-mono text-xs uppercase tracking-[0.25em]">Loading the board…</p>}
        <div className="mt-10 grid grid-cols-2 gap-x-4 gap-y-10 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5" data-testid="models-grid">
          {shown.map((m, i) => <ModelCard key={m.slug} model={m} index={i} />)}
        </div>
        {!loading && !shown.length && <p className="mt-16 font-serif text-2xl italic" data-testid="models-empty">No faces match your search.</p>}
      </div>
    </main>
  );
}
